import classNames from 'classnames';
import React, {
  Dispatch,
  FC,
  KeyboardEvent,
  ReactNode,
  SetStateAction,
} from 'react';

import Icon from '../Icon/Icon';

import style from './Checkbox.module.scss';

export interface CheckboxProps {
  onCheck: Dispatch<SetStateAction<boolean>>;
  defaultChecked?: boolean;
  name: string;
  className?: string;
  label?: string;
  children?: ReactNode;
  darkMode?: boolean;
}

interface HiddenCheckboxProps {
  defaultChecked: boolean;
  name: string;
}

interface StyledCheckboxProps {
  defaultChecked: boolean;
  darkMode: boolean;
}

const HiddenCheckbox: FC<HiddenCheckboxProps> = ({ defaultChecked, name }) => (
  <input
    // use defaultChecked for hidden input
    // since it has no event handlers
    defaultChecked={defaultChecked}
    type="checkbox"
    className={style.hiddenInput}
    name={name}
    aria-hidden={true}
  />
);

const StyledCheckbox: FC<StyledCheckboxProps> = ({
  defaultChecked,
  darkMode,
}) => (
  <div
    className={classNames(
      style.checkmark,
      defaultChecked && style.checked,
      darkMode && style.dark
    )}
  >
    {defaultChecked && (
      <Icon size={18} name="checkmark-simple--white" alt="checkmark" />
    )}
  </div>
);

export const Checkbox: FC<CheckboxProps> = ({
  children,
  className,
  darkMode,
  defaultChecked,
  label,
  name,
  onCheck,
}: CheckboxProps) => {
  function onClick(): void {
    onCheck(!defaultChecked);
  }

  function onKeyUp(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.code === ' ' || e.code === 'Enter') {
      onCheck(!defaultChecked);
    }
  }

  return (
    <div
      data-testid="OneUI-Checkbox"
      className={classNames(style.Checkbox__container, className)}
      onClick={onClick}
      onKeyUp={onKeyUp}
      role="checkbox"
      aria-checked={defaultChecked}
      tabIndex={0}
    >
      <HiddenCheckbox defaultChecked={defaultChecked} name={name} />
      <StyledCheckbox defaultChecked={defaultChecked} darkMode={darkMode} />

      <label className={darkMode && style.dark} htmlFor={name}>
        {label ? label : children}
      </label>
    </div>
  );
};

export default Checkbox;
